import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';

type CaseBarChartProps = {
  chartsData?: {
    period: string;
    total_invoice_amount: string;
    total_payment_amount: string | null;
    dayNumber?: number;
    weekNumber?: number;
    monthName?: string; // changed to string for label
  }[];
  viewMode: 'week' | 'month' | 'day';
};

const COLORS = {
  invoice: '#1F3F49',
  payment: '#6AB187',
};

// ✅ Format label for X-axis based on viewMode
const formatDateLabel = (item: any, viewMode: string): string => {
  const formatShort = (dateStr: string) =>
    dayjs(dateStr.trim()).format('DD MMM');

  const formatFull = (dateStr: string) =>
    dayjs(dateStr.trim()).format('DD/MM/YYYY');

  const period = item?.period;

  if (!period) return '';

  if (viewMode === 'week') {
    return `Week ${item.weekNumber ?? ''}`;
  } else if (viewMode === 'month') {
    return item.monthName ?? '';
  } else {
    // day view
    if (period.includes('–')) {
      const [start, end] = period.split('–').map((p: string) => p.trim());
      return `${formatFull(start)} – ${formatFull(end)}`;
    }
    return formatShort(period);
  }
};

// ✅ Tooltip formatter
const CustomTooltip = ({ active, payload, label,period }: any) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: 10,
          fontSize: '12px',
        }}
      >
        <strong>{payload[0]?.period}</strong>
        <p style={{ margin: 0 }}>
          Invoice: £{payload[0]?.value?.toLocaleString() ?? 0}
        </p>
        <p style={{ margin: 0 }}>
          Payment: £{payload[1]?.value?.toLocaleString() ?? 0}
        </p>
      </div>
    );
  }
  return null;
};

const CaseFinanceBarChart: React.FC<CaseBarChartProps> = ({
  chartsData = [],
  viewMode,
}) => {
  const processedData = chartsData.map((item) => ({
    name: formatDateLabel(item, viewMode),
    invoice: Number(item.total_invoice_amount || 0),
    payment: Number(item.total_payment_amount || 0),
    period: item.period || 'N/A', // keep original period for tooltip
  }));

  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer>
        <BarChart
          data={processedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={70}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="invoice"
            name="Invoice"
            fill={COLORS.invoice}
            stackId="finance"
          />
          <Bar
            dataKey="payment"
            name="Payment"
            fill={COLORS.payment}
            stackId="finance"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CaseFinanceBarChart;
